import ReactTooltip from "react-tooltip";
import { useState } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { getClassifierApiUrl } from "@utils/baseUrl";
import CustomToast from "./customToast";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { getUserActivePlan } from "@helpers/profileHelper";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/browser";
import accessJustificationImg from "@assets/images/access_justification.svg";
import { BRAND } from "@core/brand/brand-config";

const IS_SOURCE_ATLAS = BRAND.key === "source_atlas";

const DownloadJustification = ({ rowData, disabled = false, refreshTable }) => {
  const [loading, setLoading] = useState(false);
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");

  const downloadJustificationReport = async () => {
    setLoading(true);
    trademoTrackingEvents(trackingEventDictionary.classifiedProductJustificationDownload, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    try {
      const payload = {
        file_id: rowData?.file_id,
      };
      const response = await Axios.post(getClassifierApiUrl("intel/reportdownload"), payload, {
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"];
      let filename = `${rowData?.country}_${rowData?.classification_type}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (refreshTable) {
        refreshTable();
      }
    } catch (error) {
      let errorMessage = "Something Went Wrong!!!";

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          toast.error("Error parsing error response:", parseError);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      Sentry.captureException(error);
      toast(<CustomToast title={errorMessage} customId="error" />, {
        autoClose: 5000,
        draggable: false,
        closeButton: false,
        className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = (e) => {
    if (disabled || loading) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    downloadJustificationReport();
  };

  return (
    <div className="tw-flex tw-items-center">
      <div data-for="justification-report-cell" data-tip={disabled ? "Not available for pending/failed classifications" : rowData?.is_download === false && !IS_SOURCE_ATLAS ? "Downloading justification costs 3 value add credits" : ""} onClick={handleDownloadClick} className={`tw-border tw-border-gray-200 tw-rounded-lg tw-py-1 tw-px-2 tw-flex tw-gap-1 ${disabled || loading ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`}>
        {rowData?.is_download === true ? (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
              <path d="M480-336 288-528l51-51 105 105v-342h72v342l105-105 51 51-192 192ZM263.72-192Q234-192 213-213.15T192-264v-72h72v72h432v-72h72v72q0 29.7-21.16 50.85Q725.68-192 695.96-192H263.72Z" />
            </svg>
            <span className="tw-text-gray-900 tw-text-xs tw-font-medium">{loading ? "Downloading..." : "Download"}</span>
          </>
        ) : (
          <>
            <img src={accessJustificationImg} alt="access_justification" />
            <span className="tw-text-gray-900 tw-text-xs tw-font-medium">{loading ? "Accessing..." : "Get Access"}</span>
          </>
        )}
      </div>
      {(disabled || rowData?.is_download === false) && <ReactTooltip className="trademo-tooltip !tw-rounded-full !tw-bg-[#fff6ed] !tw-border !tw-border-[#dc6803] !tw-text-[#dc6803] !tw-py-1 !tw-px-4" place="bottom" id="justification-report-cell" effect="solid" />}
    </div>
  );
};

export default DownloadJustification;
